import { useEffect, useRef } from 'react';
import {
  copyDocumentStyles,
  createBodyThemeMirror,
  createHeadStyleMirror,
} from '../utils/popoutWindowStyles.utils';
import { notifyWidgetHostChanged } from 'utils/widgets/widgetHost.utils';

const getResolvedThemeColor = (variableName, fallbackColor) => {
  try {
    const value = window.getComputedStyle(document.body).getPropertyValue(variableName).trim();
    return value || fallbackColor;
  } catch (error) {
    return fallbackColor;
  }
};

// Moves a persistent React portal host into a separate same-origin window.
// Keeping the host and portal identity stable preserves widget-local state as
// well as Redux/RTK Query subscriptions. Calls onRequestReturn when the window
// closes or fails so the owner can restore the host to its dashboard cell.
const WidgetWindowPortal = ({
  title = 'Widget',
  popoutWindow,
  contentElement,
  dashboardContainer,
  onRequestReturn,
}) => {
  const onRequestReturnRef = useRef(onRequestReturn);
  onRequestReturnRef.current = onRequestReturn;

  useEffect(() => {
    let hasReturned = false;

    const requestReturn = () => {
      if (hasReturned) return;
      hasReturned = true;
      onRequestReturnRef.current?.();
    };

    const cleanupCallbacks = [];
    const runCleanupCallbacks = () => {
      while (cleanupCallbacks.length) {
        try {
          cleanupCallbacks.pop()();
        } catch (error) {
          // Ignore teardown errors from an already-closed window.
        }
      }
    };

    try {
      const childDocument = popoutWindow.document;
      childDocument.title = title;

      try {
        copyDocumentStyles(document, childDocument);
        cleanupCallbacks.push(createHeadStyleMirror(document, childDocument));
        cleanupCallbacks.push(createBodyThemeMirror(document.body, childDocument.body));
      } catch (error) {
        console.error('Failed to copy styles into widget pop-out window:', error);
      }

      const backgroundColor = getResolvedThemeColor('--color-base', '#1c2733');
      const fontColor = getResolvedThemeColor('--color-font', '#ffffff');

      childDocument.body.style.margin = '0';
      childDocument.body.style.background = backgroundColor;
      contentElement.classList.add('p-2');
      contentElement.style.overflow = 'hidden';
      contentElement.style.background = backgroundColor;
      contentElement.style.color = fontColor;
      childDocument.body.appendChild(contentElement);
      notifyWidgetHostChanged(contentElement);

      if (typeof popoutWindow.addEventListener === 'function') {
        popoutWindow.addEventListener('pagehide', requestReturn, { once: true });
        cleanupCallbacks.push(() => popoutWindow.removeEventListener('pagehide', requestReturn));
      }

      const closeChildWindow = () => popoutWindow.close();
      window.addEventListener('pagehide', closeChildWindow);
      cleanupCallbacks.push(() => window.removeEventListener('pagehide', closeChildWindow));
    } catch (error) {
      console.error('Failed to prepare widget pop-out window:', error);
      runCleanupCallbacks();
      try {
        popoutWindow.close();
      } catch (closeError) {
        // Ignore: setup may have failed because the window already closed.
      }
      requestReturn();
    }

    return () => {
      hasReturned = true;
      runCleanupCallbacks();
      if (dashboardContainer && contentElement) {
        contentElement.classList.remove('p-2');
        contentElement.style.removeProperty('overflow');
        contentElement.style.removeProperty('background');
        contentElement.style.removeProperty('color');
        dashboardContainer.appendChild(contentElement);
        notifyWidgetHostChanged(contentElement);
      }
      try {
        popoutWindow.close();
      } catch (error) {
        // Ignore: the window may already be gone.
      }
    };
    // Window identity is fixed for the lifetime of this manager instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (contentElement?.ownerDocument && contentElement.ownerDocument !== document) {
      contentElement.ownerDocument.title = title;
    }
  }, [contentElement, title]);

  return null;
};

export default WidgetWindowPortal;
